import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList,
  Calendar,
  CheckCircle2,
  Phone,
  RotateCcw,
  Package,
  Send,
  MapPin,
  FileText,
  ArrowRight,
  User as UserIcon,
} from "lucide-react";
import { fetchAssignedByMeTasks } from "../lib/api";
import { Skeleton } from "./ui/skeleton";

const TYPE_META = {
  call_back:      { label: "Call back",      Icon: Phone,     cls: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" },
  follow_up:      { label: "Follow up",      Icon: RotateCcw, cls: "text-sky-300    border-sky-500/30    bg-sky-500/10" },
  send_sample:    { label: "Send sample",    Icon: Package,   cls: "text-amber-300  border-amber-500/30  bg-amber-500/10" },
  send_proposal:  { label: "Send proposal",  Icon: Send,      cls: "text-violet-300 border-violet-500/30 bg-violet-500/10" },
  schedule_visit: { label: "Schedule visit", Icon: MapPin,    cls: "text-pink-300   border-pink-500/30   bg-pink-500/10" },
  other:          { label: "Other",          Icon: FileText,  cls: "text-zinc-300   border-zinc-700/40   bg-zinc-500/10" },
};

const TONE_CLS = {
  rose:    "text-rose-300    bg-rose-500/10    border-rose-500/30",
  amber:   "text-amber-300   bg-amber-500/10   border-amber-500/30",
  emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  zinc:    "text-zinc-400    bg-zinc-500/10    border-zinc-700/40",
};

function formatDueDate(dueIso) {
  if (!dueIso) return null;
  const due = new Date(dueIso + "T00:00:00");
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / 86400000);
  if (diff === 0) return { label: "due today",    tone: "amber" };
  if (diff === 1) return { label: "due tomorrow", tone: "amber" };
  if (diff < 0)   return { label: `${-diff}d overdue`, tone: "rose" };
  if (diff <= 7)  return {
    label: `due ${due.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
    tone: "emerald",
  };
  return {
    label: `due ${due.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
    tone: "zinc",
  };
}

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60)        return "just now";
  if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Assigned Tasks — admin/dispatcher Dashboard tile that surfaces work
 * the current user has delegated to others. Recently completed tasks
 * bubble to the top (so admin sees fresh wins), with still-open
 * assignments listed below sorted by due_date.
 */
export function AssignedTasksCard({ limit = 8 }) {
  const [tasks, setTasks] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      fetchAssignedByMeTasks({ limit })
        .then((data) => { if (!cancelled) setTasks(data); })
        .catch(() => { if (!cancelled) setTasks([]); });
    load();
    // Poll lightly so newly-completed work surfaces without a refresh.
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [limit]);

  if (tasks === null) {
    return (
      <div className="panel p-5" data-testid="assigned-tasks-card">
        <Skeleton className="h-32 rounded-md bg-zinc-900/50" />
      </div>
    );
  }

  const completed = tasks.filter((t) => t.status === "done").length;
  const open = tasks.filter((t) => t.status === "todo").length;
  const overdue = tasks.filter((t) => {
    if (t.status !== "todo") return false;
    const d = formatDueDate(t.due_date);
    return d?.tone === "rose";
  }).length;

  return (
    <div className="panel p-5" data-testid="assigned-tasks-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <ClipboardList className="w-4 h-4 text-indigo-400" />
          <h3 className="text-sm font-semibold tracking-tight text-zinc-100">
            Assigned Tasks
          </h3>
          {completed > 0 && (
            <span
              data-testid="assigned-completed-count"
              className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
            >
              {completed} done
            </span>
          )}
          {open > 0 && (
            <span
              data-testid="assigned-open-count"
              className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/30"
            >
              {open} open
            </span>
          )}
          {overdue > 0 && (
            <span
              data-testid="assigned-overdue-count"
              className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30"
            >
              {overdue} overdue
            </span>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-[0.12em] font-mono text-zinc-500">
          Last 14 days
        </span>
      </div>

      {tasks.length === 0 ? (
        <div className="text-xs text-zinc-500 italic py-3" data-testid="assigned-tasks-empty">
          You haven't assigned any tasks to staff yet. Use{" "}
          <Link to="/admin/users" className="text-emerald-400 hover:text-emerald-300">
            Bulk Tasks
          </Link>{" "}
          or the Assign-to picker on any dispensary's task form.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {tasks.map((t) => {
            const meta = TYPE_META[t.task_type] || TYPE_META.other;
            const TypeIcon = meta.Icon;
            const done = t.status === "done";
            const due = !done ? formatDueDate(t.due_date) : null;
            const assigneeLabel = t.assignee_name || t.assignee_email || "Unassigned";
            return (
              <li key={t.id}>
                <Link
                  to={`/dispensaries/${t.dispensary_id}`}
                  data-testid={`assigned-task-row-${t.id}`}
                  className={`group flex items-start gap-3 rounded-md border px-3 py-2 transition-colors ${
                    done
                      ? "border-emerald-500/20 bg-emerald-500/5 hover:bg-emerald-500/10"
                      : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-700 hover:bg-zinc-900/60"
                  }`}
                >
                  <span
                    className={`shrink-0 mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded border ${
                      done
                        ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                        : meta.cls
                    }`}
                  >
                    {done ? (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    ) : (
                      <TypeIcon className="w-3 h-3" />
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm truncate ${done ? "text-zinc-400 line-through" : "text-zinc-100"}`}>
                      {t.title}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1.5">
                      <UserIcon className="w-2.5 h-2.5" />
                      <span className="text-zinc-300">{assigneeLabel}</span>
                      <span className="text-zinc-600">·</span>
                      <span className="truncate">{t.dispensary_name}</span>
                    </div>
                  </div>
                  {done ? (
                    <span
                      className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                      title={t.completed_at}
                    >
                      <CheckCircle2 className="w-2.5 h-2.5" />
                      {relativeTime(t.completed_at)}
                    </span>
                  ) : (
                    due && (
                      <span
                        className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${TONE_CLS[due.tone]}`}
                      >
                        <Calendar className="w-2.5 h-2.5" />
                        {due.label}
                      </span>
                    )
                  )}
                  <ArrowRight className="shrink-0 w-4 h-4 text-zinc-600 group-hover:text-zinc-300" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default AssignedTasksCard;
